import { useState, useRef, useEffect } from 'react';
import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query';
import { Bell, Megaphone, Archive, X, Mail, MessageCircle, Activity } from 'lucide-react';
import { queryKeys } from '../lib/queryKeys';
import {
  fetchNotifications,
  fetchNotificationsByTab,
  archiveAllNotifications,
} from '../api/notifications';
import { LeadUnreadBadge } from './LeadUnreadBadge.jsx';
import { LeadJournalPopup } from './LeadJournalPopup.jsx';

const REFETCH_INTERVAL = 60_000;

function kindOf (type) {
  if (type === 'inbound_message') return 'message';
  if (type === 'reminder') return 'reminder';
  return 'system';
}

function resolveChannel (notification) {
  return notification.channel || notification.payload?.channel || null;
}

function ChannelIcon ({ notification }) {
  const kind = kindOf(notification.type);
  if (kind === 'message') {
    const ch = resolveChannel(notification);
    if (ch === 'email') return <Mail size={16} strokeWidth={1.6} />;
    return <MessageCircle size={16} strokeWidth={1.6} />;
  }
  return <Megaphone size={16} strokeWidth={1.6} />;
}

function formatTime (dateStr) {
  const d = new Date(dateStr);
  const now = new Date();
  const diffMs = now - d;
  const diffMin = Math.floor(diffMs / 60000);
  if (diffMin < 1) return 'только что';
  if (diffMin < 60) return `${diffMin}м`;
  const diffHours = Math.floor(diffMin / 60);
  if (diffHours < 24) return `${diffHours}ч`;
  const diffDays = Math.floor(diffHours / 24);
  return `${diffDays}д`;
}

function formatCount (n) {
  if (!n || n <= 0) return null;
  return n > 99 ? '99+' : String(n);
}

const FILTERS = [
  { id: 'all',        label: 'Все',         Icon: Bell },
  { id: 'messages',   label: 'Сообщения',   Icon: Bell },
  { id: 'reminders',  label: 'Напоминания', Icon: Megaphone },
  { id: 'monitoring', label: 'Мониторинг',  Icon: Activity },
  { id: 'archive',    label: 'Архив',       Icon: Archive },
];

export function NotificationBell ({ onOpenLead, label }) {
  const [isOpen, setIsOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('all');
  const [popupLead, setPopupLead] = useState(null);
  const modalRef = useRef(null);
  const qc = useQueryClient();

  const { data: badgeData } = useQuery({
    queryKey: queryKeys.notifications.list(),
    queryFn: () => fetchNotifications(30),
    refetchInterval: REFETCH_INTERVAL,
    staleTime: 10_000,
  });
  const unreadTotal = badgeData?.unreadCount ?? badgeData?.counts?.total ?? 0;

  const { data: tabData, isLoading: tabLoading } = useQuery({
    queryKey: queryKeys.notifications.byTabGrouped(activeTab),
    queryFn: () => fetchNotificationsByTab({
      tab: activeTab === 'all' ? undefined : activeTab,
      groupBy: 'lead',
      limit: 100,
    }),
    enabled: isOpen,
    staleTime: 10_000,
  });
  const groups = tabData?.groups || [];

  const markAllMut = useMutation({
    mutationFn: archiveAllNotifications,
    onSuccess: () => qc.invalidateQueries({ queryKey: queryKeys.notifications.all }),
  });

  useEffect(() => {
    if (!isOpen) return;
    const onMouseDown = (e) => {
      if (popupLead) return;
      if (modalRef.current && !modalRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    const onKey = (e) => { if (e.key === 'Escape' && !popupLead) setIsOpen(false); };
    document.addEventListener('mousedown', onMouseDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onMouseDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [isOpen, popupLead]);

  const badgeText = formatCount(unreadTotal);

  return (
    <>
      <button
        type="button"
        className="notification-bell__button"
        onClick={() => setIsOpen(true)}
        title="Уведомления"
      >
        <span className="notification-bell__icon-wrap">
          <Bell size={20} strokeWidth={1.6} />
          {badgeText && (
            <span className="notification-bell__badge">{badgeText}</span>
          )}
        </span>
        {label && <span className="notification-bell__label">{label}</span>}
      </button>

      {isOpen && (
        <div className="notification-modal" ref={modalRef} role="dialog" aria-label="Уведомления">
            <div className="notification-modal__header">
              <span className="notification-modal__title">Уведомления</span>
              <button type="button" className="notification-modal__readall"
                onClick={() => markAllMut.mutate()} disabled={markAllMut.isPending}>
                Прочитать все
              </button>
              <button
                type="button"
                className="notification-modal__close"
                onClick={() => setIsOpen(false)}
                aria-label="Закрыть"
              >
                <X size={18} strokeWidth={1.6} />
              </button>
            </div>

            <div className="notification-modal__tabs">
              {FILTERS.map(({ id, label, Icon }) => {
                const isActive = activeTab === id;
                return (
                  <button
                    key={id}
                    type="button"
                    className={`notification-modal__tab${isActive ? ' notification-modal__tab--active' : ''}`}
                    onClick={() => { setActiveTab(id); }}
                  >
                    <Icon size={16} strokeWidth={1.6} />
                    <span>{label}</span>
                  </button>
                );
              })}
            </div>

            <div className="notification-modal__list">
              {tabLoading ? (
                <div className="notification-modal__empty">Загрузка…</div>
              ) : groups.length === 0 ? (
                <div className="notification-modal__empty">Нет уведомлений</div>
              ) : groups.map((g) => {
                const key = g.leadId || '__system__';
                const openPopup = () => { if (g.leadId) setPopupLead({ leadId: g.leadId, leadName: g.leadName }); };
                return (
                  <div
                    key={key}
                    className={`notification-modal__item${g.unreadCount > 0 ? ' notification-modal__item--unread' : ''}`}
                    onClick={openPopup}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => { if (e.key === 'Enter') openPopup(); }}
                  >
                    <div className="notification-modal__item-icon">
                      <ChannelIcon notification={g.lastNotification} />
                    </div>
                    <div className="notification-modal__item-body">
                      <div className="notification-modal__item-title">
                        {g.leadName || 'Без лида'}{g.count > 1 ? ` · ${g.count} событий` : ''}
                      </div>
                      {g.lastNotification?.body && (
                        <div className="notification-modal__item-text">{g.lastNotification.body}</div>
                      )}
                      <div className="notification-modal__item-time">{formatTime(g.lastNotification?.createdAt)}</div>
                    </div>
                    <LeadUnreadBadge count={g.unreadCount} />
                  </div>
                );
              })}
            </div>
        </div>
      )}
      {popupLead && (
        <LeadJournalPopup
          leadId={popupLead.leadId}
          leadName={popupLead.leadName}
          onClose={() => setPopupLead(null)}
          onOpenLead={onOpenLead}
        />
      )}
    </>
  );
}
